import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import {
  listIodUsers, saveIodUser, toggleIodUserStatus,
  getIodUserHistory, getAllIodDealers,
  getIodCompanies, getIodRegions, getIodDivisions,
} from '../../services/iod/iod-admin-service';
import { listIodRoles } from '../../services/iod/iod-admin-service';
import styles from './iod-admin.module.css';

// Roles that require Division + Company + Business Partner tabs
const MULTI_TAB_ROLES = ['commercialUser', 'dealer', 'transporter', 'MIS'];
const TABS = ['User Info', 'Division', 'Company', 'Business Partner'];

const EMPTY_FORM = {
  userId: '', userName: '', roleCode: '',
  divisions: [], companies: [], regions: [], dealers: [],
  status: 'Active',
};

export default function IodUserMaster() {
  const [users, setUsers]           = useState([]);
  const [roles, setRoles]           = useState([]);
  const [allDivisions, setAllDivisions] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [allRegions, setAllRegions]   = useState([]);
  const [allDealers, setAllDealers]   = useState([]);

  const [loading, setLoading]       = useState(false);
  const [showModal, setShowModal]   = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [activeTab, setActiveTab]   = useState(0);
  const [errors, setErrors]         = useState({});

  const [showHistory, setShowHistory]   = useState(false);
  const [historyData, setHistoryData]   = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage]   = useState(1);
  const HISTORY_PAGE_SIZE = 10;

  const [writeAccess, setWriteAccess] = useState(false);

  // ── Load data ──────────────────────────────────────────────────────────────
  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listIodUsers();
      setUsers(res.data || []);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    Promise.all([
      listIodRoles(),
      getIodDivisions(),
      getIodCompanies(),
      getIodRegions(),
      getAllIodDealers(),
    ]).then(([r, d, c, reg, dl]) => {
      setRoles(r.data || []);
      setAllDivisions((d.data || []).map(x => x.divisionCode || x));
      setAllCompanies((c.data || []).map(x => x.companyCode || x));
      setAllRegions((reg.data || []).map(x => x.regionCode || x));
      setAllDealers(dl.data || []);
    }).catch(() => {});

    // Write access check
    try {
      const sessions = JSON.parse(sessionStorage.getItem('AuthorizedSessions') || '{}');
      const hasWrite = Object.values(sessions).flat()
        .some(m => m.submenu === 'User Master' && m.write);
      setWriteAccess(hasWrite);
    } catch { setWriteAccess(false); }
  }, [loadUsers]);

  const isMultiTab = MULTI_TAB_ROLES.includes(form.roleCode);

  // ── Modal open/close ───────────────────────────────────────────────────────
  const handleAdd = () => {
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setActiveTab(0);
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({
      userId:    user.userId || '',
      userName:  user.userName || '',
      roleCode:  user.roleCode || '',
      divisions: user.divisions || [],
      companies: user.companies || [],
      regions:   user.regions || [],
      dealers:   user.dealers || [],
      status:    user.status || 'Active',
    });
    setActiveTab(0);
    setErrors({});
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setActiveTab(0);
  };

  // ── Tab navigation with validation ────────────────────────────────────────
  const validateUserInfo = () => {
    const e = {};
    if (!form.userId.trim())   e.userId   = 'User ID is required';
    if (!form.userName.trim()) e.userName = 'User Name is required';
    if (!form.roleCode)        e.roleCode = 'Role is required';
    if (!form.regions.length)  e.regions  = 'Select at least one Region';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (activeTab === 0 && !validateUserInfo()) return;
    if (activeTab === 1 && isMultiTab && !form.divisions.length) {
      toast.warn('Select at least one Division'); return;
    }
    if (activeTab === 2 && isMultiTab && !form.companies.length) {
      toast.warn('Select at least one Company'); return;
    }
    setActiveTab(t => t + 1);
  };

  // ── Toggle checkbox arrays ─────────────────────────────────────────────────
  const toggleItem = (field, value) => {
    setForm(prev => {
      const arr = prev[field];
      return {
        ...prev,
        [field]: arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value],
      };
    });
  };

  const toggleAll = (field, allItems) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].length === allItems.length ? [] : [...allItems],
    }));
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validateUserInfo()) { setActiveTab(0); return; }
    if (isMultiTab && !form.divisions.length) { toast.warn('Select at least one Division'); setActiveTab(1); return; }
    if (isMultiTab && !form.companies.length) { toast.warn('Select at least one Company'); setActiveTab(2); return; }

    // Duplicate check on Add — compare against already-loaded users list
    if (!editingUser) {
      const isDuplicate = users.some(
        u => (u.userId || u.emailId || '').toUpperCase() === form.userId.trim().toUpperCase()
      );
      if (isDuplicate) {
        setErrors(prev => ({ ...prev, userId: `User '${form.userId.trim()}' already exists. Use Edit to modify.` }));
        setActiveTab(0);
        return;
      }
    }

    setLoading(true);
    try {
      const res = await saveIodUser({ ...form, isEdit: !!editingUser });
      if (res.statusCode === '0000') {
        toast.success(res.statusMsg || 'Saved successfully');
        handleClose();
        loadUsers();
      } else {
        toast.error(res.statusMsg || 'Save failed');
      }
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Status toggle ──────────────────────────────────────────────────────────
  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
    setLoading(true);
    try {
      const res = await toggleIodUserStatus(user.userId, newStatus);
      if (res.statusCode === '0000') {
        toast.success('Status updated');
        loadUsers();
      } else {
        toast.error(res.statusMsg || 'Update failed');
      }
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  // ── History ────────────────────────────────────────────────────────────────
  const handleHistory = async (user) => {
    setHistoryPage(1);
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const res = await getIodUserHistory(user.userId);
      setHistoryData((res.data || []).sort((a, b) => new Date(b.modifiedDate) - new Date(a.modifiedDate)));
    } catch {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const historySlice = historyData.slice((historyPage - 1) * HISTORY_PAGE_SIZE, historyPage * HISTORY_PAGE_SIZE);
  const historyTotalPages = Math.ceil(historyData.length / HISTORY_PAGE_SIZE) || 1;

  // ── Render tabs ────────────────────────────────────────────────────────────
  const renderTabBar = () => {
    const visibleTabs = isMultiTab ? TABS : [TABS[0]];
    return (
      <div className={styles.tabBar}>
        {visibleTabs.map((t, i) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            className={`${styles.tab} ${activeTab === i ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(i)}
          >
            {t}
          </button>
        ))}
      </div>
    );
  };

  const renderUserInfoTab = () => (
    <div>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>User ID *</label>
        <input
          className={`${styles.formControl} ${errors.userId ? styles.error : ''}`}
          value={form.userId}
          onChange={e => setForm(p => ({ ...p, userId: e.target.value }))}
          readOnly={!!editingUser}
          placeholder="e.g. ashwini"
          data-testid="input-userId"
        />
        {errors.userId && <div className={styles.errorText}>{errors.userId}</div>}
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>User Name *</label>
        <input
          className={`${styles.formControl} ${errors.userName ? styles.error : ''}`}
          value={form.userName}
          onChange={e => setForm(p => ({ ...p, userName: e.target.value }))}
          placeholder="Full Name"
          data-testid="input-userName"
        />
        {errors.userName && <div className={styles.errorText}>{errors.userName}</div>}
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Role *</label>
        <select
          className={`${styles.formControl} ${errors.roleCode ? styles.error : ''}`}
          value={form.roleCode}
          onChange={e => setForm(p => ({ ...p, roleCode: e.target.value, divisions: [], companies: [] }))}
          data-testid="select-roleCode"
        >
          <option value="">-- Select Role --</option>
          {roles.map(r => (
            <option key={r.roleCode || r} value={r.roleCode || r}>
              {r.roleName || r.roleDesc || r.roleCode || r}
            </option>
          ))}
        </select>
        {errors.roleCode && <div className={styles.errorText}>{errors.roleCode}</div>}
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Region *</label>
        {errors.regions && <div className={styles.errorText}>{errors.regions}</div>}
        <div className={styles.checkGrid}>
          {allRegions.map(r => (
            <label key={r} className={styles.checkItem}>
              <input
                type="checkbox"
                checked={form.regions.includes(r)}
                onChange={() => toggleItem('regions', r)}
              />
              {r}
            </label>
          ))}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Status</label>
        <select
          className={styles.formControl}
          value={form.status}
          onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>
    </div>
  );

  const renderDivisionTab = () => (
    <div>
      <label className={`${styles.selectAllRow}`}>
        <input
          type="checkbox"
          checked={form.divisions.length === allDivisions.length && allDivisions.length > 0}
          onChange={() => toggleAll('divisions', allDivisions)}
        />
        Select All Divisions
      </label>
      <div className={styles.checkGrid}>
        {allDivisions.map(d => (
          <label key={d} className={styles.checkItem}>
            <input
              type="checkbox"
              checked={form.divisions.includes(d)}
              onChange={() => toggleItem('divisions', d)}
            />
            {d}
          </label>
        ))}
      </div>
    </div>
  );

  const renderCompanyTab = () => (
    <div>
      <label className={styles.selectAllRow}>
        <input
          type="checkbox"
          checked={form.companies.length === allCompanies.length && allCompanies.length > 0}
          onChange={() => toggleAll('companies', allCompanies)}
        />
        Select All Companies
      </label>
      <div className={styles.companyGrid}>
        {allCompanies.map(c => (
          <div
            key={c}
            className={`${styles.companyTile} ${form.companies.includes(c) ? styles.companyTileSelected : ''}`}
            onClick={() => toggleItem('companies', c)}
            data-testid={`company-tile-${c}`}
          >
            {c}
          </div>
        ))}
      </div>
    </div>
  );

  const renderDealerTab = () => (
    <div>
      <label className={styles.selectAllRow}>
        <input
          type="checkbox"
          checked={form.dealers.length === allDealers.length && allDealers.length > 0}
          onChange={() => toggleAll('dealers', allDealers.map(d => d.dealerCode))}
        />
        Select All Business Partners
      </label>
      <div className={styles.checkGrid}>
        {allDealers.map(d => (
          <label key={d.dealerCode} className={styles.checkItem}>
            <input
              type="checkbox"
              checked={form.dealers.includes(d.dealerCode)}
              onChange={() => toggleItem('dealers', d.dealerCode)}
            />
            {d.dealerCode} — {d.dealerName}
          </label>
        ))}
      </div>
    </div>
  );

  const tabContent = [renderUserInfoTab, renderDivisionTab, renderCompanyTab, renderDealerTab];
  const isLastTab = !isMultiTab || activeTab === TABS.length - 1;

  return (
    <div className={styles.pageWrap}>
      {loading && (
        <div className={styles.spinnerOverlay}>
          <Spinner animation="border" style={{ color: '#810055' }} />
        </div>
      )}

      <div className={styles.pageTitle}>User Master</div>
      <div className={styles.pageSubTitle}>Add or Edit IOD User Details</div>

      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          {writeAccess && (
            <button className={styles.btnPrimary} onClick={handleAdd} data-testid="btn-add-user">
              Add User
            </button>
          )}
          <span className={styles.hint}>* Double-click row to edit</span>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Status</th>
              <th>User ID</th>
              <th>User Name</th>
              <th>Role</th>
              <th>Regions</th>
              <th>Divisions</th>
              <th>Modified By</th>
              <th>Modified On</th>
              {writeAccess && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={9} className={styles.emptyState}>No users found</td></tr>
            ) : users.map(u => (
              <tr key={u.userId} onDoubleClick={() => writeAccess && handleEdit(u)}>
                <td>
                  <span className={u.status === 'Active' ? styles.badgeActive : styles.badgeInactive}>
                    {u.status}
                  </span>
                </td>
                <td>{u.userId}</td>
                <td>{u.userName}</td>
                <td>{u.roleCode}</td>
                <td>{(u.regions || []).join(', ')}</td>
                <td>{(u.divisions || []).join(', ')}</td>
                <td>{u.modifiedBy}</td>
                <td>{u.modifiedDate ? new Date(u.modifiedDate).toLocaleDateString('en-IN') : ''}</td>
                {writeAccess && (
                  <td>
                    <button className={styles.actionBtn} onClick={() => handleEdit(u)}>Edit</button>
                    <button className={styles.actionBtn} onClick={() => handleToggleStatus(u)}>
                      {u.status === 'Active' ? 'Deactivate' : 'Activate'}
                    </button>
                    <button className={styles.actionBtn} onClick={() => handleHistory(u)}>History</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Add / Edit Modal ── */}
      <Modal show={showModal} onHide={handleClose} size="lg" backdrop="static">
        <Modal.Header closeButton style={{ background: '#810055', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '16px' }}>
            {editingUser ? `Edit User — ${editingUser.userId}` : 'Add New User'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderTabBar()}
          {(isMultiTab ? tabContent : [tabContent[0]])[activeTab]?.()}
        </Modal.Body>
        <Modal.Footer>
          <button className={styles.btnSecondary} onClick={handleClose}>Cancel</button>
          {!isLastTab && (
            <button className={styles.btnOutline} onClick={handleNext}>Next →</button>
          )}
          {isLastTab && (
            <button className={styles.btnPrimary} onClick={handleSave} disabled={loading}>
              {loading ? 'Saving…' : 'Save'}
            </button>
          )}
        </Modal.Footer>
      </Modal>

      {/* ── History Modal ── */}
      <Modal show={showHistory} onHide={() => setShowHistory(false)} size="xl" backdrop="static">
        <Modal.Header closeButton style={{ background: '#810055', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '16px' }}>User History</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spinner animation="border" style={{ color: '#810055' }} />
            </div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>User ID</th><th>User Name</th><th>Role</th>
                    <th>Regions</th><th>Divisions</th><th>Companies</th>
                    <th>Status</th><th>Modified By</th><th>Modified On</th>
                  </tr>
                </thead>
                <tbody>
                  {historySlice.map((h, i) => (
                    <tr key={i}>
                      <td>{h.userId}</td>
                      <td>{h.userName}</td>
                      <td>{h.roleCode}</td>
                      <td>{(h.regions || []).join(', ')}</td>
                      <td>{(h.divisions || []).join(', ')}</td>
                      <td>{(h.companies || []).join(', ')}</td>
                      <td>
                        <span className={h.status === 'Active' ? styles.badgeActive : styles.badgeInactive}>
                          {h.status}
                        </span>
                      </td>
                      <td>{h.modifiedBy}</td>
                      <td>{h.modifiedDate ? new Date(h.modifiedDate).toLocaleDateString('en-IN') : ''}</td>
                    </tr>
                  ))}
                  {historySlice.length === 0 && (
                    <tr><td colSpan={9} className={styles.emptyState}>No history found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <div className={styles.paginationBar}>
          <span>{(historyPage - 1) * HISTORY_PAGE_SIZE + 1}–{Math.min(historyPage * HISTORY_PAGE_SIZE, historyData.length)} of {historyData.length}</span>
          <div className={styles.paginationBtns}>
            <ChevronLeft
              size={18}
              style={{ cursor: historyPage === 1 ? 'default' : 'pointer', color: historyPage === 1 ? '#ccc' : '#333' }}
              onClick={() => historyPage > 1 && setHistoryPage(p => p - 1)}
            />
            <span>Page {historyPage} of {historyTotalPages}</span>
            <ChevronRight
              size={18}
              style={{ cursor: historyPage === historyTotalPages ? 'default' : 'pointer', color: historyPage === historyTotalPages ? '#ccc' : '#333' }}
              onClick={() => historyPage < historyTotalPages && setHistoryPage(p => p + 1)}
            />
          </div>
        </div>
        <Modal.Footer>
          <button className={styles.btnSecondary} onClick={() => setShowHistory(false)}>Close</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
